import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";

const Signup = ({ setUser }) => {
  const { role, clubToken } = useParams();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clubId, setClubId] = useState("");
  const [clubName, setClubName] = useState("");
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clubLocked, setClubLocked] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const navigate = useNavigate();

  // Block supervisor signup without club token
  useEffect(() => {
    if (role === 'supervisor' && !clubToken) {
      toast.error("Supervisors must use the unique signup link provided by the admin");
      navigate("/");
    }
  }, [role, clubToken, navigate]);

  // Password validation function
  const validatePassword = (pwd) => {
    const errors = [];
    const strength = { score: 0, label: "", color: "" };

    if (pwd.length < 8) {
      errors.push("At least 8 characters");
    } else {
      strength.score += 1;
    }

    if (!/[A-Z]/.test(pwd)) {
      errors.push("One uppercase letter");
    } else {
      strength.score += 1;
    }

    if (!/[a-z]/.test(pwd)) {
      errors.push("One lowercase letter");
    } else {
      strength.score += 1;
    }

    if (!/[0-9]/.test(pwd)) {
      errors.push("One number");
    } else {
      strength.score += 1;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      errors.push("One special character (!@#$%^&* etc.)");
    } else {
      strength.score += 1;
    }

    // Calculate strength label and color
    if (strength.score === 5) {
      strength.label = "Strong";
      strength.color = "bg-green-500";
    } else if (strength.score >= 3) {
      strength.label = "Medium";
      strength.color = "bg-yellow-500";
    } else if (strength.score > 0) {
      strength.label = "Weak";
      strength.color = "bg-red-500";
    }

    setPasswordErrors(errors);
    setPasswordStrength(strength);
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    validatePassword(pwd);
  };

  useEffect(() => {
    // If club token is provided, fetch that specific club
    if (clubToken) {
      axios.get(`${API}/clubs/by-token/${clubToken}`)
        .then((response) => {
          setClubId(response.data.id);
          setClubName(response.data.name);
          setClubLocked(true);
          toast.success(`Joining ${response.data.name}`);
        })
        .catch((error) => {
          toast.error("Invalid signup link");
          // Still fetch all clubs as fallback
          axios.get(`${API}/clubs`).then((response) => {
            setClubs(response.data);
          }).catch(console.error);
        });
    } else {
      // Fetch all clubs if no token
      axios.get(`${API}/clubs`).then((response) => {
        setClubs(response.data);
      }).catch(console.error);
    }
  }, [clubToken]);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!clubId && (role === 'volunteer' || role === 'supervisor')) {
      toast.error("Please select a club");
      return;
    }

    // Validate password
    if (passwordErrors.length > 0) {
      toast.error("Please meet all password requirements");
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup`, {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        role,
        club_id: clubId || null
      });
      
      // Check if volunteer account is pending (HTTP 202)
      if (response.status === 202) {
        // Navigate to pending approval page
        toast.info("Account created! Waiting for supervisor approval.");
        navigate("/pending-approval", {
          state: {
            email: email,
            clubName: clubName || clubs.find(c => c.id === clubId)?.name
          }
        });
        return;
      }
      
      // Handle successful signup (supervisor/admin with token)
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      // Handle other errors
      if (error.response?.status === 202) {
        // Fallback for 202 if caught as error
        toast.info("Account created! Waiting for supervisor approval.");
        navigate("/pending-approval", {
          state: {
            email: email,
            clubName: clubName || clubs.find(c => c.id === clubId)?.name
          }
        });
      } else {
        toast.error(error.response?.data?.detail || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-amber-50 to-red-100 p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(220,38,38,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(251,191,36,0.1),transparent_50%)]" />
      
      {/* Back to Home Link */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 z-20 text-red-600 hover:text-red-700 font-medium flex items-center gap-2 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-red-100">
        <CardHeader className="space-y-4 text-center">
          <Link to="/" className="mx-auto w-32 h-32 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
            <img 
              src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
              alt="PlayBack Token Logo" 
              className="w-full h-full object-contain"
            />
          </Link>
          <div>
            <CardTitle className="text-3xl font-bold text-red-600 brand-font">Join PlayBack Token</CardTitle>
            <CardDescription className="text-base mt-2">
              Sign up as {role === 'volunteer' ? 'a Volunteer' : 'a Supervisor'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
                <Input
                  id="firstName"
                  data-testid="signup-firstname-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                  className="border-red-200 focus:border-red-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">Last Name</Label>
                <Input
                  id="lastName"
                  data-testid="signup-lastname-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                  required
                  className="border-red-200 focus:border-red-400"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john.smith@example.com"
                required
                className="border-red-200 focus:border-red-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Create a password"
                required
                className="border-red-200 focus:border-red-400"
              />
              {/* Password Strength Indicator */}
              {password && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    {passwordStrength.label && (
                      <span className={`text-xs font-medium ${
                        passwordStrength.score === 5 ? 'text-green-600' : 
                        passwordStrength.score >= 3 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    )}
                  </div>
                  {/* Password Requirements */}
                  {passwordErrors.length > 0 && (
                    <div className="text-xs text-red-600 space-y-1">
                      <p className="font-medium">Password must contain:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {passwordErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
              <Input
                id="confirmPassword"
                data-testid="signup-confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                className="border-red-200 focus:border-red-400"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-600">Passwords do not match</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="club" className="text-sm font-medium">Your Club</Label>
              {clubLocked ? (
                <div className="border-2 border-green-300 bg-green-50 rounded-lg p-3 flex items-center justify-between">
                  <span className="font-semibold text-gray-800">{clubName}</span>
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">Pre-selected</span>
                </div>
              ) : (
                <>
                  <Select value={clubId} onValueChange={setClubId} required>
                    <SelectTrigger data-testid="signup-club-select" className="border-red-200 focus:border-red-400">
                      <SelectValue placeholder="Choose a club" />
                    </SelectTrigger>
                    <SelectContent>
                      {clubs.map((club) => (
                        <SelectItem key={club.id} value={club.id} data-testid={`club-option-${club.id}`}>
                          {club.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {clubs.length === 0 && (
                    <p className="text-xs text-amber-600">No clubs available. Please contact admin.</p>
                  )}
                </>
              )}
            </div>
            <Button 
              type="submit" 
              data-testid="signup-submit-button"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-6 rounded-full shadow-lg hover:shadow-xl"
              disabled={loading || (!clubLocked && clubs.length === 0)}
            >
              {loading ? "Creating account..." : "Sign Up"}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-red-600 hover:text-red-700 font-medium">
                Login here
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;
